import React, { useState, useRef, useEffect } from 'react';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';

import {
    Viewer,
    MapboxImageryProvider,
    MapboxStyleImageryProvider,
    EllipsoidTerrainProvider,
    SceneMode,
    ProviderViewModel,
    buildModuleUrl,
} from 'cesium';

import { MapControl } from 'controllers';
import { MAPBOX_ACCESS_TOKEN } from 'config';

const useStyles = makeStyles(() =>
    createStyles({
        root: {
            width: '100%',
            height: '100%',
            flex: 1,
        },
    }),
);

interface Props {
    registerMapControl?: (
        register: (control: MapControl) => void,
        view: Viewer,
    ) => void;
    options?: Viewer.ConstructorOptions;
}

const defaultProviderViewModel = new ProviderViewModel({
    name: 'Satellite',
    iconUrl: 'Widgets/Images/ImageryProviders/mapboxSatellite.png',
    tooltip: 'Mapbox Satellite',
    creationFunction: () => {
        return new MapboxImageryProvider({
            mapId: 'mapbox.satellite',
            accessToken: MAPBOX_ACCESS_TOKEN,
        });
    },
});

const imageryViewModels: ProviderViewModel[] = [
    defaultProviderViewModel,
    new ProviderViewModel({
        name: 'Satellite Streets',
        iconUrl: 'Widgets/Images/ImageryProviders/mapboxSatellite.png',
        tooltip: 'Mapbox Satellite with Streets',
        creationFunction: () => {
            return new MapboxStyleImageryProvider({
                styleId: 'clob9qw4z007r01r74fh97hbk',
                username: 'michael-bauer-geoplex',
                accessToken: MAPBOX_ACCESS_TOKEN,
            });
        },
    }),
    new ProviderViewModel({
        name: 'Streets',
        iconUrl: 'Widgets/Images/ImageryProviders/mapboxStreets.png',
        tooltip: 'Mapbox Street',
        creationFunction: () => {
            return new MapboxStyleImageryProvider({
                styleId: 'cloba1zr200by01riey2u9osr',
                username: 'michael-bauer-geoplex',
                accessToken: MAPBOX_ACCESS_TOKEN,
            });
        },
    }),
    new ProviderViewModel({
        name: 'Streets Dark',
        iconUrl: 'Widgets/Images/ImageryProviders/mapboxStreets.png',
        tooltip: 'Mapbox Street',
        creationFunction: () => {
            return new MapboxStyleImageryProvider({
                styleId: 'cloba5bwo00cy01rc7iij69zt',
                username: 'michael-bauer-geoplex',
                accessToken: MAPBOX_ACCESS_TOKEN,
            });
        },
    }),
];

const terrainViewModels: ProviderViewModel[] = [
    new ProviderViewModel({
        name: 'GS84 Ellipsoid',
        iconUrl: buildModuleUrl(
            'Widgets/Images/TerrainProviders/Ellipsoid.png',
        ),
        tooltip: 'WGS84 standard ellipsoid, also known as EPSG:4326',
        creationFunction: () => {
            return new EllipsoidTerrainProvider();
        },
    }),
];

const MapView: React.FunctionComponent<Props> = ({
    registerMapControl,
    options,
}) => {
    const classes = useStyles();
    const mapRef = useRef<HTMLDivElement>(null);

    const [view, setView] = useState<Viewer>();

    const handleMapCommands = (control?: MapControl) => {
        if (!control || !view) return;
        control.apply(view, handleMapCommands);
    };

    useEffect(() => {
        if (view && registerMapControl)
            registerMapControl(handleMapCommands, view);
    }, [view]);

    useEffect(() => {
        let viewer: Viewer | undefined;

        if (mapRef.current && !view) {
            mapRef.current.innerHTML = '';
            viewer = new Viewer(mapRef.current, {
                shouldAnimate: true,
                sceneMode: SceneMode.SCENE3D,
                homeButton: false,
                imageryProviderViewModels: imageryViewModels,
                selectedImageryProviderViewModel: defaultProviderViewModel,
                terrainProviderViewModels: terrainViewModels,
                selectedTerrainProviderViewModel: terrainViewModels[0],
                ...options,
            });
            console.log('Creating a Cesium View');

            setView(viewer);
        }

        return () => {
            if (view && !mapRef.current) {
                console.log('Destroying Cesium View');
                view.destroy();
                setView(undefined);
            }
        };
    }, [mapRef.current, options]);

    return <div className={classes.root} ref={mapRef} />;
};

export default MapView;
